import { useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { Artist, DayContext, Id, PlanBlock } from '../../engine/types.ts';
import { fmtRange, fmtTime, HOUR } from '../../engine/time.ts';
import { timelineLanes, timelineRange, validTimelineSet } from '../../engine/timeline.ts';
import { stageColor, stageColor2 } from '../theme.ts';
import '../../styles/timeline.css';

const HOUR_WIDTH = 188;
const STAGE_WIDTH = 104;
const LANE_HEIGHT = 92;
const compactTime = (ts: number) => fmtTime(ts).replace(':00', '');

export function ScheduleTimeline({
  ctx,
  blocks,
  artistById,
  pickedSetIds,
  onSetClick,
}: {
  ctx: DayContext;
  blocks: PlanBlock[];
  artistById: Map<Id, Artist>;
  pickedSetIds: Set<Id>;
  onSetClick: (setId: Id) => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const [only, setOnly] = useState<Id[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const helpId = useId();
  const scrollId = useId();

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    scroller.current?.scrollTo({ left: 0, top: 0 });
    setOnly([]);
  }, [ctx.day.id]);

  const sets = ctx.sets.filter((set) => validTimelineSet(set) &&
    ctx.stages.some((stage) => stage.id === set.stageId && !stage.isNonMusic));
  const running = ctx.stages.filter((stage) => !stage.isNonMusic &&
    sets.some((set) => set.stageId === stage.id));
  const chosen = only.filter((id) => running.some((stage) => stage.id === id));
  const stages = chosen.length ? running.filter((stage) => chosen.includes(stage.id)) : running;
  const range = timelineRange(sets);
  if (!range) return <p className="muted">No sets on this day yet. Paste a grid on the import screen.</p>;

  const { start, end, hours } = range;
  const width = hours * HOUR_WIDTH;
  const position = (ts: number) => ((ts - start) / HOUR) * HOUR_WIDTH;
  const isNow = now >= start && now < end;
  const attendance = new Map(blocks.flatMap((block) => block.setId ? [[block.setId, block] as const] : []));
  const ticks = Array.from({ length: hours + 1 }, (_, index) => start + index * HOUR);
  const toggleStage = (id: Id) => setOnly((previous) => previous.includes(id)
    ? previous.filter((item) => item !== id) : [...previous, id]);
  const scrollHours = (direction: number) => scroller.current?.scrollBy({ left: direction * HOUR_WIDTH });

  return (
    <section className="schedule-timeline" aria-label={`${ctx.day.label} timeline`}>
      <div className="stagepicker" role="group" aria-label="Stages shown in timeline">
        <button type="button" className={`chip ${chosen.length === 0 ? 'on' : ''}`}
          aria-pressed={chosen.length === 0} onClick={() => setOnly([])}>
          All {running.length}
        </button>
        {running.map((stage) => (
          <button type="button" key={stage.id}
            className={`chip ${chosen.includes(stage.id) ? 'on' : ''}`}
            aria-pressed={chosen.includes(stage.id)}
            style={{ '--stage': stageColor(stage.id) } as CSSProperties}
            onClick={() => toggleStage(stage.id)}>
            {stage.name}
          </button>
        ))}
      </div>
      <div className="timeline-tools" role="group" aria-label="Move through time">
        <button type="button" className="small ghost" aria-controls={scrollId}
          onClick={() => scrollHours(-1)}>← Earlier</button>
        <button type="button" className="small ghost" aria-controls={scrollId}
          onClick={() => scrollHours(1)}>Later →</button>
        {isNow && <button type="button" className="small ghost" aria-controls={scrollId}
          onClick={() => scroller.current?.scrollTo({ left: Math.max(0, position(now) - HOUR_WIDTH / 2) })}>
          Jump to now
        </button>}
      </div>
      <p id={helpId} className="timeline-help muted">Swipe across time. Tap a set to pick it. Times are Eastern.</p>
      <div ref={scroller} id={scrollId} className="timeline-scroll" tabIndex={0}
        role="region" aria-label="Scrollable festival timeline" aria-describedby={helpId}
        style={{ '--timeline-stage-width': `${STAGE_WIDTH}px` } as CSSProperties}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            scrollHours(event.key === 'ArrowLeft' ? -1 : 1);
          } else if (event.key === 'Home' || event.key === 'End') {
            event.preventDefault();
            scroller.current?.scrollTo({ left: event.key === 'Home' ? 0 : width });
          }
        }}>
        <div className="timeline-canvas" style={{ width: STAGE_WIDTH + width }}>
          <div className="timeline-ruler">
            <div className="timeline-corner">Stage / ET</div>
            <div className="timeline-hours" style={{ width }}>
              {ticks.map((ts, index) => (
                <time key={ts} dateTime={new Date(ts).toISOString()}
                  className={index === hours ? 'timeline-hour last' : 'timeline-hour'}
                  style={{ left: position(ts) }}>{compactTime(ts)}</time>
              ))}
              {isNow && <span className="timeline-now-label" style={{ left: position(now) }}>Now</span>}
            </div>
          </div>
          {stages.map((stage) => {
            const entries = timelineLanes(sets.filter((set) => set.stageId === stage.id));
            const lanes = Math.max(1, ...entries.map(({ lane }) => lane + 1));
            return (
              <div key={stage.id} className="timeline-row"
                style={{ '--stage': stageColor(stage.id), '--stage2': stageColor2(stage.id) } as CSSProperties}>
                <h3 className="timeline-stage">{stage.name}</h3>
                <div className="timeline-track" style={{ width, height: lanes * LANE_HEIGHT }}>
                  {ticks.map((ts) => <span key={ts} className="timeline-rule" aria-hidden="true"
                    style={{ left: position(ts) }} />)}
                  {isNow && <span className="timeline-now" aria-hidden="true" style={{ left: position(now) }} />}
                  {entries.map(({ set, lane }) => {
                    const name = artistById.get(set.artistId)?.name ?? set.artistId;
                    const block = attendance.get(set.id);
                    const picked = pickedSetIds.has(set.id);
                    const partial = picked && block && (block.startTs > set.startTs || block.endTs < set.endTs);
                    const attended = partial ? `${fmtRange(block.startTs, block.endTs)}` : '';
                    const label = `${name}, ${stage.name}, ${fmtRange(set.startTs, set.endTs)}${set.billingNote ? `, ${set.billingNote}` : ''}${partial ? `. Picked for ${attended}` : picked ? '. Picked' : ''}. ${picked ? 'Remove from' : 'Add to'} my plan`;
                    return (
                      <button type="button" key={set.id}
                        className={`timeline-set${picked ? partial ? ' partial' : ' planned' : ''}`}
                        style={{ left: position(set.startTs), width: Math.max(1, position(set.endTs) - position(set.startTs) - 3), top: lane * LANE_HEIGHT + 6, height: LANE_HEIGHT - 12 }}
                        aria-label={label} aria-pressed={picked} title={label}
                        onFocus={(event) => {
                          const container = scroller.current;
                          if (!container) return;
                          const buttonRect = event.currentTarget.getBoundingClientRect();
                          const containerRect = container.getBoundingClientRect();
                          const visibleStart = containerRect.left + STAGE_WIDTH + 6;
                          if (buttonRect.left < visibleStart) container.scrollLeft -= visibleStart - buttonRect.left;
                          else if (buttonRect.right > containerRect.right) container.scrollLeft += buttonRect.right - containerRect.right;
                        }}
                        onClick={() => onSetClick(set.id)}>
                        <span className="timeline-name">{picked ? '✓ ' : ''}{name}</span>
                        <span className="timeline-when">{compactTime(set.startTs)}–{compactTime(set.endTs)}</span>
                        {partial && <span className="timeline-partial">Part: {compactTime(block.startTs)}–{compactTime(block.endTs)}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="legend"><span>✓ Picked</span><span>Gold edge: part of the set</span></div>
    </section>
  );
}
